import { Component, computed, Inject, OnInit, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDrawerMode, MatSidenav } from '@angular/material/sidenav';
import { MenuService } from '../../services/menu/menu.service';
import {
  APP_CONFIG,
  APP_VERSION,
  AppConfig,
  EnvironmentService,
  SmoothViewportScroller
} from '@smartcharge/shared/index';
import { MatDialog } from '@angular/material/dialog';
import { StationSearchDialogSimpleComponent } from '../stations/station-search/station-search-dialog-simple/station-search-dialog-simple.component';
import { AuthService } from '../../services/auth/auth.service';
import { UserService } from '../../lib';
import { FormControl } from '@angular/forms';
import { RolesService } from '../../services/roles/roles.service';
import { UserType } from '../../user-roles';
import { Router } from '@angular/router';
import { adminMenuItems } from '../admin/admin-menu-items';
import { CompanyService } from '../../services/company/company.service';
import { AuthAsService } from '../../services/auth/auth-as/auth-as.service';
import { MatSelectChange } from '@angular/material/select';

@Component({
  selector: 'sc-main-sidenav',
  templateUrl: './main-sidenav.component.html',
  styleUrls: ['./main-sidenav.component.scss'],

  host: {
    '(window:resize)': 'onWindowResize($event)'
  },

  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class MainSidenavComponent implements OnInit {
  sidebar = viewChild<MatSidenav>('mat-sidenav');

  adminView = signal<boolean>(false);
  drawerMode = signal<MatDrawerMode>('over');
  drawerOpened = signal<boolean>(true);
  loggedInAs = signal<boolean>(false);
  selectedCompany = new FormControl(null);
  showBell = signal<boolean>(false);

  environment = computed(() => this.environmentService.environment());
  menuCompanies = computed(() => {
    if (this.adminView()) {
      return null;
    }

    return this.menuService.companies();
  });
  menuItems = computed(() => {
    if (this.adminView()) {
      return adminMenuItems;
    }

    return this.menuService.menuItems();
  });
  user = computed(() => this.userService.user());

  readonly UserType = UserType;

  private headwayConfig = {
    selector: '#headway-badge',
    account: 'yZmo1J',
    callbacks: {
      onWidgetReady: () => {
        this.showBell.set(true);
      }
    }
  };

  constructor(
    @Inject(APP_CONFIG) readonly appConfig: AppConfig,
    @Inject(APP_VERSION) readonly appVersion: string,
    protected readonly companyService: CompanyService,
    protected readonly environmentService: EnvironmentService,
    private readonly authService: AuthService,
    private readonly authAsService: AuthAsService,
    private readonly dialog: MatDialog,
    private readonly menuService: MenuService,
    private readonly rolesService: RolesService,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly viewportScroller: SmoothViewportScroller,
    private readonly window: Window
  ) {
    if (!this.menuService.isCached()) {
      this.authService.refreshMenus();
    }

    if (this.router.url.includes('/admin') && this.rolesService.hasHighlevelAccess(UserType.Admin)) {
      this.adminView.set(true);
    }

    if (localStorage.getItem('loggedInUser')) {
      this.loggedInAs.set(true);
    }

    this.menuService.menus$.subscribe(() => {
      const companies = this.menuService.companies().filter((company) => company.Menus.length);

      if (companies.length) {
        const selectedCompanyID = localStorage.getItem('selectedCompany');
        const selectedCompany = companies.find((company) => company.CompanyID === +selectedCompanyID);

        this.selectedCompany.setValue(selectedCompany || companies[0]);
        this.selectedCompany.updateValueAndValidity();
      }
    });
  }

  ngOnInit() {
    this.onWindowResize();

    // Init Headway
    setTimeout(() => {
      (window as any).Headway.init(this.headwayConfig);
    }, 1000);
  }

  companyEquals(val1, val2): boolean {
    return val1 && val2 && val1.CompanyID === val2.CompanyID;
  }

  logout() {
    this.authService.logout();
  }

  logoutAs() {
    this.authAsService.logout();
  }

  onCompanyChange(event: MatSelectChange) {
    localStorage.setItem('selectedCompany', event.value.CompanyID);

    const companyIds = this.menuService
      .companies()
      .map((company) => company.CompanyID)
      .join('|');
    const isRouteCompany = new RegExp(`/companies/(${companyIds})/([a-z-]+)`, 'gi').exec(this.router.url);

    if (isRouteCompany) {
      const newUrl = this.router.url.replace(
        `/companies/${isRouteCompany[1]}/`,
        `/companies/${event.value.CompanyID}/`
      );

      this.router.navigateByUrl(newUrl);
    }
  }

  onMenuScroll(event: Event): void {
    const target = event.target as HTMLElement;

    if (Math.floor(target.scrollHeight - target.scrollTop) <= target.clientHeight) {
      target.classList.add('scroll-bottom');
    } else {
      target.classList.remove('scroll-bottom');
    }
  }

  toggleDrawer() {
    this.drawerOpened.update((state) => !state);
  }

  toggleSearch() {
    this.dialog.open(StationSearchDialogSimpleComponent);
  }

  protected onWindowResize(event?: Event): void {
    const window = event ? (event.target as Window) : this.window;

    if (window.innerWidth < 1200) {
      this.drawerOpened.set(false);
      this.drawerMode.set('over');
      this.viewportScroller.setOffset([0, 64]);
    } else {
      this.drawerOpened.set(true);
      this.drawerMode.set('side');
      this.viewportScroller.setOffset([0, 0]);
    }
  }
}
